import React, { useState, useEffect } from 'react';
import api from '../../core/api/models';
import { Loader } from '../common';
import Podium from '../Podium';
import ResponsiveTable from '../common/ResponsiveTable';
import { toOrdinal } from '../../utilities/number';
import ResultsTableProps, { Results } from './type';
import './styles.scss';

const riderRowHeader = ({ values }: Results, index: number) => (
  <div className={`
      results-table__row-header
      ${index >= 10 ? 'results-table__row-header--small' : ''}
    `.trim()}
  >
    {values.rank && (
      <div
        className="results-table__row-position"
        aria-label={values.rank ? `${toOrdinal(values.rank)} position` : ''}
      >
        {values.rank}
      </div>
    )}
    <div className="results-table__row-person">
      <div className="results-table__row-firstname">{values.firstname}</div>
      <div className="results-table__row-lastname">{values.lastname}</div>
      {values.team && (
        <div className="results-table__row-team">{values.team}</div>
      )}
    </div>
  </div>
);

const teamRowHeader = ({ values }: Results, index: number) => (
  <div className={`results-table__row-header ${index >= 10 ? 'results-table__row-header--small' : ''}`}>
    <div
      className="results-table__row-position"
      aria-label={values.rank ? `${toOrdinal(values.rank)} position` : ''}
    >
      {values.rank}
    </div>
    <div className="results-table__row-country">{values.team}</div>
  </div>
);

const ResultsTable = ({
  title,
  headers,
  results,
  podium,
  asyncRequest,
  nested,
}: ResultsTableProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(!!asyncRequest);
  const [apiError, setApiError] = useState<string>();
  const [resultData, setResultData] = useState({
    title,
    headers,
    results,
    podium,
  });

  const getRow = (result: Results) => {
    // Remove this hacky mutation once the Russia situation has blown over. See this ticket: https://stadion-uci.atlassian.net/browse/UCIKB-410
    let modifiedResult = result;
    if (modifiedResult.values.nationality === 'RUS' || modifiedResult.values.nationality === 'BLR') {
      modifiedResult = {
        ...result,
        values: {
          ...result.values,
          nationality: '***',
        },
      };
    }
    return ({
      row: modifiedResult.values,
      ...((modifiedResult.table
        && { Table: <ResultsTable nested {...modifiedResult.table} /> }) || {}),
    });
  };

  const getRowHeader = (result: Results, index: number) => {
    if (result.headerType === 'team') {
      return teamRowHeader(result, index);
    }
    if (result.headerType === 'rider') {
      return riderRowHeader(result, index);
    }

    return <div />;
  };

  const fetchResults = async () => {
    try {
      const data = await api.calendar.getResult({
        eventCode: asyncRequest?.eventCode as string,
        raceType: asyncRequest?.raceType as string,
        raceName: asyncRequest?.raceName as string || undefined,
        disciplineCode: asyncRequest?.disciplineCode as string,
      });

      setResultData({
        ...resultData,
        ...data,
      });
      setIsLoading(false);
    } catch (error) {
      setApiError(error as string);
    }
  };

  useEffect(() => {
    if (asyncRequest) {
      if (asyncRequest.onBeforeLoad) {
        asyncRequest.onBeforeLoad();
      }

      fetchResults().then(asyncRequest.onLoad);
    }
  }, []);

  return (
    <div className={`results-table ${nested ? 'results-table--nested' : ''}`}>
      {resultData.title && (
        <h4 className="results-table__title">{resultData.title}</h4>
      )}

      {resultData.podium && (
        <>
          <Podium podium={resultData.podium.podium} discipline={resultData.podium.discipline} />

          <div className="results-table__separator" />
        </>
      )}

      {apiError && (
        <div className="results-table__api-error">
          <h3>{apiError}</h3>
        </div>
      )}

      {isLoading && (
        <div className="results-table__loader">
          <Loader />
        </div>
      )}

      {!isLoading && (
        <ResponsiveTable
          headers={resultData.headers}
          rows={resultData.results.map(getRow) as {
            row: Record<string, string | number>, Table?: JSX.Element
          }[]}
          stickyCol={resultData.results.map(getRowHeader)}
          className="results-table__table"
          moreClassName="results-table__table-more"
          nested={nested}
        />
      )}
    </div>
  );
};

export default ResultsTable;
